import { faCircleInfo } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { useAtom, useSetAtom } from 'jotai';

import { useGetCountry } from '@/utils/internationalization/useGetCountry';

import type { PreferencesResponse } from '../../../../@types/preferences';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetRecommendationName } from '../../../../modules/patientList/patientStrip/patientStripOdontogram/utils';
import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '../../../../react-query/usePreferences';
import type { TProcedure } from '../../../../react-query/useVisitSummaries';
import { greyLight300 } from '../../../../theme/videa-style-tokens';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { isIndicatorPreferenceOpen } from '../../atoms';

const RecommendationPref = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding: ${({ theme }) => theme.spacing(2)};
  outline: 1px solid ${greyLight300};
  border-radius: 10px;
  width: 100%;
  box-sizing: border-box;

  .pref-toggle-section {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    background-color: ${greyLight300};
    border-radius: 0.5em;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    gap: 0.5em;
    padding: 1em 0.75em 1em;
  }

  .toggle-label {
    width: 13em;
  }

  .toggle-helper {
    display: inline-block;
    min-width: 12em;
    font-size: 0.8em;
    font-weight: 600;
    color: gray;
    display: flex;
    justify-content: flex-end;
  }

  .language-toggle {
    font-size: 0.8em;
    font-weight: 600;
    color: gray;
    width: 8.5em;
  }

  .recommendation-description {
    font-size: 0.8em;
    padding-right: 0.5em;
    overflow: auto;
  }
`;

const RecommendationLanguageContainer = styled(RecommendationPref)`
  padding: 0;
  outline: none;
  width: auto;
  font-size: 0.75em;
  gap: 0;
  .pref-toggle-section {
  }
  .toggle-helper {
    margin-left: auto;
  }
  .recommendation-description {
    padding-left: 1em;
  }
`;

interface Props {
  preferenceName: keyof PreferencesResponse;
  procedureName: TProcedure['name'];
}

const RecommendationControl = ({ preferenceName, procedureName }: Props) => {
  const { mutate: preferencesUpdate } = usePreferencesPracticeUpdate({}, true);
  const { trackEvent } = useTrackEvent();
  const { data: prefs } = usePreferences();
  const { t } = useLingui();
  const { getRecommendationName } = useGetRecommendationName();

  const handleChange = (checked: boolean) => {
    trackEvent(mixpanelEvents.huddleIndicatorPreferenceToggle, {
      indicatorType: procedureName,
      value: checked,
    });
    preferencesUpdate({ [preferenceName]: checked });
  };

  const getRecommendationDescription = (name: TProcedure['name']) => {
    switch (name) {
      case 'enamelRegen':
        return t`Interproximal carious lesion detection(s) contained within the enamel. Does not trigger if there is also a carious lesion detected on the tooth that extends into dentin.`;
      case 'filling':
        return t`At least one carious lesion detection that extends into dentin and has adequate tooth structure remaining to support a direct restoration.`;
      case 'crown':
        return t`Restorable teeth with moderate tooth structure loss.`;
      case 'rootCanal':
      case 'pulpotomy':
        return t`Restorable teeth with moderate tooth structure loss where the pulp of the tooth has been compromised or a periapical radiolucency has been detected.`;
      case 'aligner':
        return t`Adult patients with mild to moderate spacing or crowding in an arch (2-8mm) and the absence of severe periodontal disease.`;
      case 'extraction':
        return t`Non-restorable teeth with severe tooth structure loss and/or severe periodontal disease.`;
      case 'implant':
        return t`Adult patients with sufficient mesiodistal interproximal space (7+mm) who have missing teeth that have not been replaced, or teeth that need extraction but have no treatment plan for replacement.`;
      case 'perio':
      case 'locPerio':
        return t`Teeth display evidence of potential radiographic bone loss, as defined by AAP guidelines. Flag will not appear for patients under 18.`;
      default:
        return '';
    }
  };

  const isEnabled = !!prefs?.[preferenceName];

  return (
    <RecommendationPref>
      <div className="pref-toggle-section">
        <Switch checked={isEnabled} onCheckedChange={handleChange} />
        <VideaTypography variant="labelSmall" className="toggle-label">
          {getRecommendationName(procedureName)}
        </VideaTypography>

        <span className="toggle-helper">
          {isEnabled ? t`recommendation enabled` : t`recommendation disabled`}
        </span>
      </div>

      <VideaTypography className="recommendation-description">
        {getRecommendationDescription(procedureName)}
      </VideaTypography>
    </RecommendationPref>
  );
};

const RecommendationLanguagePref = () => {
  const { mutate: preferencesUpdate } = usePreferencesPracticeUpdate({});
  const { t } = useLingui();

  const recommendationLanguage = (
    txPref: boolean,
    isCanadianPractice: boolean,
  ) => {
    // TODO: Update pulpotomy indicator copy when supported by BE
    return {
      crown: txPref ? 'Crown' : 'Significant Decay/Loss',
      incipient: txPref ? 'Incipient Tx' : 'Incipient Decay',
      filling: txPref ? 'Filling' : 'Decay/Loss into Dentin',
      // If Canadian practice the first option should be "Possible Perio"
      srp: isCanadianPractice
        ? 'Possible Perio'
        : txPref
          ? 'SRP'
          : 'Perio Assessment',
      locSrp: isCanadianPractice
        ? 'Possible Perio (localized)'
        : txPref
          ? 'SRP (localized)'
          : 'Perio Assessment (localized)',
      extraction: txPref ? 'Extraction' : 'Non-Restorable Tooth',
      implant: txPref ? 'Implant' : 'Unreplacable Tooth',
      rootCanal: txPref ? 'Root Canal' : 'Pupal Involvement',
      aligner: txPref ? 'Clear Aligner' : 'Crowding/Spacing',
    };
  };

  const { data: preferences } = usePreferences();

  const isSelectedTx =
    preferences?.indicatorCopy?.crown === 'Crown' ? true : false;

  const isCanadianPractice = useGetCountry() === 'CA';

  const { trackEvent } = useTrackEvent();

  return (
    <RecommendationLanguageContainer>
      <div className="pref-toggle-section">
        <Switch
          checked={isSelectedTx}
          onCheckedChange={(txSelected) => {
            preferencesUpdate({
              indicatorCopy: recommendationLanguage(
                txSelected,
                isCanadianPractice,
              ),
            });
            trackEvent(mixpanelEvents.huddleIndicatorLanguageToggleTx, {
              value: txSelected,
            });
          }}
        />

        <VideaTypography
          variant="labelSmall"
          className="vai-text-base vai-font-medium"
        >
          <Trans>Recommendation Wording</Trans>
        </VideaTypography>
        <span className="vai-text-sm vai-font-semibold vai-text-gray-600 vai-ml-auto">
          {isSelectedTx ? t`tx oriented` : t`dx oriented`}
        </span>
      </div>

      <div className="vai-text-sm vai-text-gray-600 vai-mt-2">
        <p>
          <Trans>
            This preference changes the wording (language) used for the
            recommendations at your practice. It does not change their
            calculation logic.
          </Trans>
        </p>
      </div>
    </RecommendationLanguageContainer>
  );
};

// SRP combined control with sub-sections for generalized and localized
const SRPRecommendationControl = () => {
  const { mutate: preferencesUpdate } = usePreferencesPracticeUpdate({}, true);
  const { trackEvent } = useTrackEvent();
  const { data: prefs } = usePreferences();
  const { t } = useLingui();
  const { getRecommendationName } = useGetRecommendationName();

  // Enabled if either is enabled
  const isEnabled = !!prefs?.srpOpportunity || !!prefs?.locSrpOpportunity;

  const handleChange = (checked: boolean) => {
    trackEvent(mixpanelEvents.huddleIndicatorPreferenceToggle, {
      indicatorType: 'perio',
      value: checked,
    });

    const updateObject: Record<string, boolean> = {};
    if (prefs?.srpIndicatorEnabled) {
      updateObject.srpOpportunity = checked;
    }
    if (prefs?.locSrpIndicatorEnabled) {
      updateObject.locSrpOpportunity = checked;
    }

    preferencesUpdate(updateObject);
  };

  return (
    <RecommendationPref>
      <div className="pref-toggle-section">
        <Switch checked={isEnabled} onCheckedChange={handleChange} />
        <VideaTypography variant="labelSmall" className="toggle-label">
          {getRecommendationName('perio')}
        </VideaTypography>
        <span className="toggle-helper">
          {isEnabled ? t`recommendation enabled` : t`recommendation disabled`}
        </span>
      </div>
      <div style={{ marginLeft: '1em' }}>
        {prefs?.showParentheticalTxNames ? (
          <>
            <div>
              <VideaTypography variant="labelSmall" style={{ fontWeight: 500 }}>
                <Trans>Generalized:</Trans>
              </VideaTypography>
              <VideaTypography className="recommendation-description">
                {t`4 or more teeth in a quadrant display evidence of potential radiographic bone loss, aligned with RBL measurement coloring of orange or red shown on the radiographs. Flag will not appear for patients under 18.`}
              </VideaTypography>
            </div>
            <div style={{ marginTop: '0.5em' }}>
              <VideaTypography variant="labelSmall" style={{ fontWeight: 500 }}>
                <Trans>Localized:</Trans>
              </VideaTypography>
              <VideaTypography className="recommendation-description">
                {t`2 or 3 teeth in a quadrant display evidence of potential radiographic bone loss, aligned with RBL measurement coloring of orange or red shown on the radiographs. Flag will not appear for patients under 18. (Note: A single tooth in a quadrant with orange/red RBL is not included in this alert due to potential variance of radiograph angulation.)`}
              </VideaTypography>
            </div>
          </>
        ) : (
          <VideaTypography className="recommendation-description">
            {t`Teeth display evidence of potential radiographic bone loss, as defined by AAP guidelines. Flag will not appear for patients under 18.`}
          </VideaTypography>
        )}
      </div>
    </RecommendationPref>
  );
};

export const RecommendationPreferenceWithTrigger = () => {
  const setIsRecommendationSettingsOpen = useSetAtom(isIndicatorPreferenceOpen);

  return (
    <>
      <Button
        onClick={() => setIsRecommendationSettingsOpen(true)}
        variant="outline"
        className="hover:vai-bg-blue-200 vai-border-blue-300 vai-text-blue-800"
      >
        <FontAwesomeIcon icon={faCircleInfo} />
        <Trans>Recommendation Preferences</Trans>
      </Button>

      <RecommendationPreferenceModal />
    </>
  );
};

export const RecommendationPreferenceModal = () => {
  const [isRecommendationSettingsOpen, setIsRecommendationSettingsOpen] =
    useAtom(isIndicatorPreferenceOpen);

  const { data: prefs } = usePreferences();

  return (
    <Dialog
      open={isRecommendationSettingsOpen}
      onOpenChange={(open) => setIsRecommendationSettingsOpen(open)}
    >
      <DialogContent className="vai-max-w-[900px] vai-w-[90vw] vai-max-h-[95vh] vai-overflow-y-auto vai-p-6">
        <DialogHeader>
          <DialogTitle className="vai-font-normal vai-text-base vai-text-gray-700 vai-mb-4">
            <Trans>User Clinical Preferences</Trans>
          </DialogTitle>
          <RecommendationLanguagePref />
        </DialogHeader>

        <hr className="vai-mx-8" />

        <p className="vai-text-sm vai-font-inter vai-font-normal">
          <Trans>
            Treatment recommendations are designed to highlight potential
            opportunities on teeth that may benefit from intervention. They are
            shown when AI detects relevant clinical findings in the
            patient&apos;s x-rays and no related treatment code has been
            recently planned or completed. These suggestions are based on
            patterns of treatment commonly taken in response to the severity,
            type, location, and context of the findings. A decision about
            appropriate treatment should always include clinical observations
            and other factors; the recommendations do not include inputs that
            are not evident on the x-rays.
          </Trans>
        </p>

        <div className="vai-grid vai-grid-cols-[repeat(auto-fit,minmax(300px,1fr))] vai-gap-4 vai-pt-2 vai-min-h-[7.5em]">
          {prefs?.fmxSummaryEnabled && (
            <RecommendationControl
              preferenceName="crownOpportunity"
              procedureName="crown"
            />
          )}
          {prefs?.incipientIndicatorEnabled && (
            <RecommendationControl
              preferenceName="incipientOpportunity"
              procedureName="enamelRegen"
            />
          )}

          {/* SRP combined control, only show when both are enabled */}
          {prefs?.srpIndicatorEnabled && prefs?.locSrpIndicatorEnabled ? (
            <SRPRecommendationControl />
          ) : prefs?.srpIndicatorEnabled ? (
            <RecommendationControl
              preferenceName="srpOpportunity"
              procedureName="perio"
            />
          ) : null}

          {prefs?.fillingIndicatorEnabled && (
            <RecommendationControl
              preferenceName="fillingOpportunity"
              procedureName="filling"
            />
          )}
          {prefs?.rootCanalIndicatorEnabled && (
            <RecommendationControl
              preferenceName="rootCanalOpportunity"
              procedureName="rootCanal"
            />
          )}
          {prefs?.pulpotomyIndicatorEnabled && (
            <RecommendationControl
              preferenceName="pulpotomyOpportunity"
              procedureName="pulpotomy"
            />
          )}
          {prefs?.extractionIndicatorEnabled && (
            <RecommendationControl
              preferenceName="extractionOpportunity"
              procedureName="extraction"
            />
          )}
          {prefs?.implantCrownIndicatorEnabled && (
            <RecommendationControl
              preferenceName="implantCrownOpportunity"
              procedureName="implant"
            />
          )}
          {prefs?.alignerIndicatorEnabled && (
            <RecommendationControl
              preferenceName="alignerOpportunity"
              procedureName="aligner"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RecommendationPreference = RecommendationPreferenceWithTrigger;
